import { useAuth } from "@clerk/clerk-react";
import { Button } from "@growth/ui";
import { CircleAlert, LoaderCircle } from "lucide-react";
import { lazy, type ReactNode, Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { FullPageMessage } from "./components/full-page-message";
import { AdminLayout } from "./layouts/admin-layout";
import { accessProblemFromError } from "./lib/access";
import { useActive } from "./lib/active";
import { apiErrorMessage } from "./lib/api-error";
import { useMe } from "./lib/me";
import { ActivityPage } from "./pages/activity";
import { AgentsPage } from "./pages/agents";
import { AiRunPage } from "./pages/ai-run";
import { AiStudioPage } from "./pages/ai-studio";
import { AuthorsPage } from "./pages/authors";
import { BlogSettingsPage } from "./pages/blog-settings";
import { CalendarPage } from "./pages/calendar";
import { ClaimsPage } from "./pages/claims";
import { ContentPage } from "./pages/content";
import { ContentSetupPage } from "./pages/content-setup";
import { InboxPage } from "./pages/inbox";
import { KnowledgePage } from "./pages/knowledge";
import { AccountPage, AccountsPage } from "./pages/leads/accounts";
import { LeadsHomePage } from "./pages/leads/home";
import { IcpPage } from "./pages/leads/icp";
import { MessagePage, OutreachPage } from "./pages/leads/outreach";
import { PeoplePage, PersonPage } from "./pages/leads/people";
import { SendingPage } from "./pages/leads/sending";
import { MembersPage } from "./pages/members";
import { NoWorkspacePage } from "./pages/no-workspace";
import { PiecePage } from "./pages/piece";
import { PlatformPage } from "./pages/platform";
import { PostEditorPage } from "./pages/post-editor";
import { PostsPage } from "./pages/posts";
import { ResearchPage } from "./pages/research";
import { SeoPage } from "./pages/seo";
import { SettingsPage } from "./pages/settings";
import { SetupPage } from "./pages/setup";
import { SignInPage } from "./pages/sign-in";
import { SignUpPage } from "./pages/sign-up";
import { SystemPage } from "./pages/system";
import { TodayPage } from "./pages/today";

// The public home page carries its own fonts and art, so it loads only when someone opens it.
const HomePage = lazy(() => import("./pages/home").then((m) => ({ default: m.HomePage })));
// So do the privacy policy and the welcome flow, which share them.
const PrivacyPage = lazy(() => import("./pages/legal").then((m) => ({ default: m.PrivacyPage })));
const WelcomePage = lazy(() => import("./pages/welcome").then((m) => ({ default: m.WelcomePage })));

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center" aria-label="Loading">
    <LoaderCircle className="size-6 animate-spin text-muted" />
  </div>
);

/**
 * Signed in with Clerk, and known to the server. People in no workspace see a "not yet" page,
 * except platform admins, who can go and create one.
 */
const RequireAccount = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { pathname } = useLocation();
  const me = useMe(Boolean(isSignedIn));

  if (!isLoaded) return <Loading />;
  // Signed-out visitors to the root see the home page; deep links still go to sign-in.
  if (!isSignedIn) return <Navigate to={pathname === "/" ? "/home" : "/sign-in"} replace />;
  if (me.isPending) return <Loading />;

  if (me.isError) {
    const problem = accessProblemFromError(me.error);
    if (problem === "unauthenticated") return <Navigate to="/sign-in" replace />;
    return (
      <FullPageMessage
        icon={<CircleAlert className="size-5" />}
        title="Couldn't open Growth Engine"
      >
        <p>
          {problem === "forbidden"
            ? apiErrorMessage(me.error)
            : "The server didn't respond. Check your connection and try again."}
        </p>
        <Button variant="secondary" onClick={() => me.refetch()}>
          Try again
        </Button>
      </FullPageMessage>
    );
  }

  if (me.data.workspaces.length === 0 && !me.data.user.isPlatformAdmin) {
    // New people make their own workspace; if sign-up is closed they wait for an invite.
    return me.data.canStartWorkspace ? <Navigate to="/welcome" replace /> : <NoWorkspacePage />;
  }
  return <Outlet />;
};

/** Signed in with Clerk (the welcome flow checks the rest itself). */
const RequireSignedIn = () => {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <Loading />;
  if (!isSignedIn) return <Navigate to="/sign-up" replace />;
  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
};

/** Pages that need a workspace. A platform admin with none is sent to create one. */
const NeedsWorkspace = ({ children }: { children: ReactNode }) => {
  const { me, workspace } = useActive();
  if (!workspace)
    return me?.user.isPlatformAdmin ? <Navigate to="/platform" replace /> : <NoWorkspacePage />;
  return <>{children}</>;
};

/**
 * Pages about one brand. A workspace without brands is sent to add one: its owner through the
 * welcome flow's "your product" step, anyone else to Settings.
 */
const NeedsBrand = ({ children }: { children: ReactNode }) => {
  const { brand, workspace } = useActive();
  const firstProduct = workspace?.role === "OWNER" && workspace.brands.length === 0;
  return (
    <NeedsWorkspace>
      {brand ? children : <Navigate to={firstProduct ? "/welcome" : "/settings"} replace />}
    </NeedsWorkspace>
  );
};

const PlatformOnly = ({ children }: { children: ReactNode }) => {
  const { me } = useActive();
  return me?.user.isPlatformAdmin ? <>{children}</> : <Navigate to="/" replace />;
};

export const AppRoutes = () => (
  <Routes>
    <Route path="/sign-in/*" element={<SignInPage />} />
    <Route path="/sign-up/*" element={<SignUpPage />} />
    <Route
      path="/privacy"
      element={
        <Suspense fallback={<Loading />}>
          <PrivacyPage />
        </Suspense>
      }
    />
    <Route element={<RequireSignedIn />}>
      <Route path="/welcome" element={<WelcomePage />} />
    </Route>
    <Route
      path="/home"
      element={
        <Suspense fallback={<Loading />}>
          <HomePage />
        </Suspense>
      }
    />
    <Route element={<RequireAccount />}>
      <Route element={<AdminLayout />}>
        <Route
          index
          element={
            <NeedsBrand>
              <PostsPage />
            </NeedsBrand>
          }
        />
        <Route
          path="posts/:id"
          element={
            <NeedsBrand>
              <PostEditorPage />
            </NeedsBrand>
          }
        />
        <Route
          path="ai"
          element={
            <NeedsBrand>
              <AiStudioPage />
            </NeedsBrand>
          }
        />
        <Route
          path="ai/runs/:id"
          element={
            <NeedsBrand>
              <AiRunPage />
            </NeedsBrand>
          }
        />
        <Route
          path="seo"
          element={
            <NeedsBrand>
              <SeoPage />
            </NeedsBrand>
          }
        />
        <Route
          path="today"
          element={
            <NeedsBrand>
              <TodayPage />
            </NeedsBrand>
          }
        />
        <Route
          path="calendar"
          element={
            <NeedsBrand>
              <CalendarPage />
            </NeedsBrand>
          }
        />
        {(
          [
            ["leads", <LeadsHomePage key="home" />],
            ["leads/icp", <IcpPage key="icp" />],
            ["leads/accounts", <AccountsPage key="accounts" />],
            ["leads/accounts/:id", <AccountPage key="account" />],
            ["leads/people", <PeoplePage key="people" />],
            ["leads/people/:id", <PersonPage key="person" />],
            ["leads/outreach", <OutreachPage key="outreach" />],
            ["leads/outreach/:id", <MessagePage key="message" />],
            ["leads/sending", <SendingPage key="sending" />],
          ] as const
        ).map(([path, page]) => (
          <Route key={path} path={path} element={<NeedsBrand>{page}</NeedsBrand>} />
        ))}
        <Route
          path="setup"
          element={
            <NeedsBrand>
              <SetupPage />
            </NeedsBrand>
          }
        />
        <Route
          path="research"
          element={
            <NeedsBrand>
              <ResearchPage />
            </NeedsBrand>
          }
        />
        <Route
          path="agents"
          element={
            <NeedsBrand>
              <AgentsPage />
            </NeedsBrand>
          }
        />
        <Route
          path="content"
          element={
            <NeedsBrand>
              <ContentPage />
            </NeedsBrand>
          }
        />
        <Route
          path="content/:id"
          element={
            <NeedsBrand>
              <PiecePage />
            </NeedsBrand>
          }
        />
        <Route
          path="inbox"
          element={
            <NeedsBrand>
              <InboxPage />
            </NeedsBrand>
          }
        />
        <Route
          path="content-setup"
          element={
            <NeedsBrand>
              <ContentSetupPage />
            </NeedsBrand>
          }
        />
        <Route
          path="knowledge"
          element={
            <NeedsBrand>
              <KnowledgePage />
            </NeedsBrand>
          }
        />
        <Route
          path="claims"
          element={
            <NeedsBrand>
              <ClaimsPage />
            </NeedsBrand>
          }
        />
        <Route
          path="blog-settings"
          element={
            <NeedsBrand>
              <BlogSettingsPage />
            </NeedsBrand>
          }
        />
        <Route
          path="authors"
          element={
            <NeedsWorkspace>
              <AuthorsPage />
            </NeedsWorkspace>
          }
        />
        <Route
          path="members"
          element={
            <NeedsWorkspace>
              <MembersPage />
            </NeedsWorkspace>
          }
        />
        <Route
          path="settings"
          element={
            <NeedsWorkspace>
              <SettingsPage />
            </NeedsWorkspace>
          }
        />
        <Route
          path="activity"
          element={
            <NeedsWorkspace>
              <ActivityPage />
            </NeedsWorkspace>
          }
        />
        <Route
          path="platform"
          element={
            <PlatformOnly>
              <PlatformPage />
            </PlatformOnly>
          }
        />
        <Route
          path="system"
          element={
            <PlatformOnly>
              <SystemPage />
            </PlatformOnly>
          }
        />
      </Route>
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);
